import {
  FC,
  KeyboardEvent,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import useOutsideClick from 'src/hooks/useOutsideClick'
import { ConversionStatusAction } from 'src/reactQuery/useSetConversionStatusMutation'
import classes from './conversionStatus.module.scss'

/**
 * Chat conversion statuses for Inbox Preview.
 *
 *   converted  – paid / booked / bought through the chat
 *   committed  – agreed to buy or reserved, payment not confirmed yet
 *
 * How loud the UI is depends on the page's `conversionDisplay` (summary
 * endpoint), so one component set serves both kinds of tenant:
 *
 *   Both modes mark each list row with a small status dot on the avatar
 *   (ConversionAvatarMarker: green check = converted, amber clock = committed).
 *   prominent  – chat sales are routine (gyms, e-commerce): the "Outcome" pill
 *                bar with counts sits above the list filters.
 *   subtle     – chat sales are rare (boutique hotels, clinics): the filter is
 *                tucked into the filters bar as a dropdown.
 *   off        – the default: unset / 'off' on the page (opt-in only, even when
 *                statuses exist). Nothing renders — no bar, dropdown or dots.
 */

export type ConversionStatus = 'converted' | 'committed'
export type ConversionDisplay = 'off' | 'subtle' | 'prominent'
export type ConversionFilterValue = 'all' | ConversionStatus | 'unconverted'
export type ConversionCounts = Partial<
  Record<Exclude<ConversionFilterValue, 'all'>, number>
>

export const CONVERSION_META: Record<
  ConversionStatus,
  { description: string; label: string }
> = {
  committed: {
    description: 'Agreed to buy — payment not confirmed yet',
    label: 'Committed',
  },
  converted: {
    description: 'Paid or booked through this chat',
    label: 'Converted',
  },
}

export const CONVERSION_FILTER_OPTIONS: {
  id: ConversionFilterValue
  label: string
}[] = [
  { id: 'all', label: 'All' },
  { id: 'converted', label: 'Converted' },
  { id: 'committed', label: 'Committed' },
  { id: 'unconverted', label: 'Unconverted' },
]

export const isConversionDisplayOn = (
  display?: ConversionDisplay | null
): display is 'subtle' | 'prominent' =>
  display === 'subtle' || display === 'prominent'

const ConvertedIcon: FC = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
    <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M5.4 8.2 7.2 10l3.4-3.6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

// Clock: agreed, waiting on payment.
const CommittedIcon: FC = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
    <circle cx="8" cy="8" r="6.25" stroke="currentColor" strokeWidth="1.5" />
    <polyline
      points="8,4.9 8,8.2 10.2,9.6"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

export const ConversionIcon: FC<{ status: ConversionStatus }> = ({ status }) =>
  status === 'converted' ? <ConvertedIcon /> : <CommittedIcon />

interface ConversionBadgeProps {
  className?: string
  placement?: 'row' | 'header'
  status: ConversionStatus
  variant: 'subtle' | 'prominent'
}

/**
 * The status marker. In the list a subtle badge is icon-only; in the panel
 * header there is room, so it always carries its label (outlined when
 * subtle, tinted when prominent).
 */
export const ConversionBadge: FC<ConversionBadgeProps> = ({
  className,
  placement = 'row',
  status,
  variant,
}) => {
  const meta = CONVERSION_META[status]
  const iconOnly = variant === 'subtle' && placement === 'row'

  return (
    <span
      aria-label={iconOnly ? meta.label : undefined}
      className={[
        classes.badge,
        classes[status],
        classes[variant],
        classes[placement],
        iconOnly ? classes.iconOnly : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      role={iconOnly ? 'img' : undefined}
      title={`${meta.label} — ${meta.description}`}
    >
      <ConversionIcon status={status} />
      {!iconOnly && <span className={classes.badgeLabel}>{meta.label}</span>}
    </span>
  )
}

const MarkerCheck: FC = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 12 12">
    <polyline
      points="3.2,6.3 5.1,8.1 8.8,4.2"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.6"
    />
  </svg>
)

const MarkerClock: FC = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 12 12">
    <circle cx="6" cy="6" r="4.6" stroke="currentColor" strokeWidth="1.1" />
    <polyline
      points="6,3.6 6,6.2 7.6,7.1"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.2"
    />
  </svg>
)

const PillCheck: FC = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 10 8">
    <polyline
      points="1,4.2 3.7,6.8 9,1.2"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.4"
    />
  </svg>
)

/**
 * Status dot overlaid on the bottom-right of a list row's avatar. The parent
 * avatar must be position: relative. Label in the tooltip / for screen readers.
 */
export const ConversionAvatarMarker: FC<{ status: ConversionStatus }> = ({
  status,
}) => {
  const meta = CONVERSION_META[status]
  return (
    <span
      aria-label={meta.label}
      className={`${classes.avatarMarker} ${classes[status]}`}
      role="img"
      title={`${meta.label} — ${meta.description}`}
    >
      {status === 'converted' ? <MarkerCheck /> : <MarkerClock />}
    </span>
  )
}

interface ConversionOutcomeBarProps {
  counts?: ConversionCounts | null
  onChange: (value: ConversionFilterValue) => void
  value: ConversionFilterValue
}

const OUTCOME_OPTIONS: { id: 'all' | ConversionStatus; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'converted', label: 'Converted' },
  { id: 'committed', label: 'Committed' },
]

/**
 * Prominent-mode filter: "Outcome" label + filled pills with counts
 * (All / Converted / Committed). Toggle buttons (aria-pressed) so it reads as
 * a single-choice filter. "All" counts every chat in scope.
 */
export const ConversionOutcomeBar: FC<ConversionOutcomeBarProps> = ({
  counts,
  onChange,
  value,
}) => {
  const total = counts
    ? (counts.converted ?? 0) +
      (counts.committed ?? 0) +
      (counts.unconverted ?? 0)
    : null

  return (
    <div
      aria-label="Filter chats by outcome"
      className={classes.outcomeBar}
      role="group"
    >
      <span className={classes.outcomeLabel}>Outcome</span>
      {OUTCOME_OPTIONS.map((option) => {
        const active = value === option.id
        const count = option.id === 'all' ? total : counts?.[option.id]
        return (
          <button
            aria-pressed={active}
            className={`${classes.outcomePill} ${classes[option.id]} ${
              active ? classes.outcomePillActive : ''
            }`}
            key={option.id}
            onClick={() => onChange(option.id)}
            type="button"
          >
            {option.id !== 'all' && (
              <span className={classes.outcomeIcon}>
                {option.id === 'converted' ? <PillCheck /> : <MarkerClock />}
              </span>
            )}
            <span>{option.label}</span>
            {typeof count === 'number' && (
              <span className={classes.outcomeCount}>
                {count.toLocaleString()}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}

/* ── Manual marking ─────────────────────────────────────────────────────── */

// "Mark as" choices, shared by the list selection bar and the chat header.
// 'none' clears the mark; the backend stores it so an automatic source can't
// re-add the status, and it reads as Unconverted everywhere.
export const CONVERSION_ACTIONS: {
  key: ConversionStatusAction
  label: string
}[] = [
  { key: 'converted', label: 'Converted' },
  { key: 'committed', label: 'Committed' },
  { key: 'none', label: 'Unconverted' },
]

const UnconvertedIcon: FC = () => (
  <svg aria-hidden="true" fill="none" viewBox="0 0 16 16">
    <circle
      cx="8"
      cy="8"
      r="6.25"
      stroke="currentColor"
      strokeDasharray="2.4 2.2"
      strokeWidth="1.5"
    />
  </svg>
)

export const ConversionActionIcon: FC<{ action: ConversionStatusAction }> = ({
  action,
}) =>
  action === 'none' ? <UnconvertedIcon /> : <ConversionIcon status={action} />

export const MarkConversionGlyph: FC<{ className?: string }> = ({
  className,
}) => (
  <svg
    aria-hidden="true"
    className={className}
    fill="none"
    height="18"
    viewBox="0 0 18 18"
    width="18"
  >
    <circle cx="9" cy="9" r="7.25" stroke="currentColor" strokeWidth="1.5" />
    <polyline
      points="5.9,9.2 8,11.3 12.2,6.9"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="1.5"
    />
  </svg>
)

interface ConversionStatusMenuProps {
  disabled?: boolean
  onSelect: (action: ConversionStatusAction) => void
  status?: ConversionStatus | null
  variant: 'subtle' | 'prominent'
}

/**
 * Chat header control: shows the current status (or "Mark conversion" when
 * there is none) and opens a menu to change it. Replaces the read-only header
 * badge wherever marking is possible.
 *
 * The menu is portaled to <body> with fixed positioning: the conversation
 * panel and its meta row are overflow:hidden, which clipped an in-place
 * dropdown so its options could not be clicked.
 */
export const ConversionStatusMenu: FC<ConversionStatusMenuProps> = ({
  disabled = false,
  onSelect,
  status,
  variant,
}) => {
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState<{
    left: number
    top: number
  } | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useOutsideClick({
    onOutsideClick: (event?: MouseEvent | TouchEvent) => {
      const target = event?.target as Node | undefined
      if (target && listRef.current?.contains(target)) return
      setOpen(false)
    },
    ref,
  })

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    setPosition({ left: rect.left, top: rect.bottom + 6 })
  }, [open])

  useEffect(() => {
    if (!open) return undefined
    const close = () => setOpen(false)
    window.addEventListener('resize', close)
    window.addEventListener('scroll', close, true)
    return () => {
      window.removeEventListener('resize', close)
      window.removeEventListener('scroll', close, true)
    }
  }, [open])

  const current: ConversionStatusAction = status ?? 'none'

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      setOpen(false)
      triggerRef.current?.focus()
    }
  }

  return (
    <div className={classes.statusMenu} onKeyDown={handleKeyDown} ref={ref}>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={
          status
            ? `Conversion: ${CONVERSION_META[status].label}. Change`
            : 'Mark conversion'
        }
        className={classes.statusMenuTrigger}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
        ref={triggerRef}
        type="button"
      >
        {status ? (
          <ConversionBadge
            placement="header"
            status={status}
            variant={variant}
          />
        ) : (
          <span className={classes.markChip}>
            <UnconvertedIcon />
            Mark conversion
          </span>
        )}
        <svg
          aria-hidden="true"
          className={`${classes.statusMenuChevron} ${open ? classes.open : ''}`}
          fill="none"
          viewBox="0 0 12 12"
        >
          <polyline
            points="3,4.5 6,7.5 9,4.5"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="1.4"
          />
        </svg>
      </button>

      {open &&
        position &&
        createPortal(
          <div
            className={classes.statusMenuList}
            ref={listRef}
            role="menu"
            style={{ left: position.left, top: position.top }}
          >
            {CONVERSION_ACTIONS.map((action) => (
              <button
                aria-checked={current === action.key}
                className={`${classes.statusMenuItem} ${classes[action.key]}`}
                disabled={disabled}
                key={action.key}
                onClick={() => {
                  setOpen(false)
                  if (action.key !== current) onSelect(action.key)
                }}
                role="menuitemradio"
                type="button"
              >
                <ConversionActionIcon action={action.key} />
                <span>
                  Mark as: <strong>{action.label}</strong>
                </span>
                {current === action.key && (
                  <span
                    aria-hidden="true"
                    className={classes.statusMenuCurrent}
                  >
                    Current
                  </span>
                )}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  )
}
